import { WizardState } from './campaign-wizard';
import { useState, useEffect } from 'preact/hooks';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { WelcomeShell } from '@course/components/sales-checkout/mirasee-welcome-page';
import { MiraseeCheckoutConfig, MiraseeWelcomePageConfig } from 'server/types/pmts-overrides';
import { resolveWelcomePageTokens } from 'shared/welcome-tokens';
import { VideoEmbed } from '@course/components/campaign/video-embed';
import { AddToCalendarDropdown } from '@components/add-to-calendar';
import { OntraportTagPicker } from './ontraport-tag-picker';
import { RichHtmlField, trimEmptyHtml } from '@components/minidoc/rich-html-field';
import {
  IcoVideoCamera,
  IcoCalendar,
  IcoBell,
  IcoExternalLink,
  IcoLock,
  IcoDocument,
  IcoSparkles,
  IcoEye,
} from '@components/icons';

type Props = {
  state: WizardState;
  updateState: (partial: Partial<WizardState>) => void;
};

const CTA_OPTIONS = [
  { value: '/community', label: 'Community feed' },
  { value: 'course', label: 'This course page' },
  { value: 'custom', label: 'Custom URL...' },
];

function resolveCtaUrl(state: WizardState): string {
  if (state.welcomeCtaUrl === 'course') {
    return `/courses/${state.courseId}`;
  }
  const url = state.welcomeCtaUrl;
  if (url && !url.startsWith('/') && !url.startsWith('http')) {
    return `https://${url}`;
  }
  return url;
}

function currentSelection(state: WizardState): string {
  if (state.welcomeCtaUrl == undefined || state.welcomeCtaUrl === '/community') {
    return '/community';
  }
  if (
    state.welcomeCtaUrl === 'course' ||
    state.welcomeCtaUrl === `/courses/${state.courseId}` ||
    state.welcomeCtaUrl === '/courses/{courseId}'
  ) {
    return 'course';
  }
  return 'custom';
}

export { resolveCtaUrl };

function AIGeneratePanel({ state, updateState }: Props) {
  const [text, setText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  async function handleGenerate() {
    if (!text.trim() || !state.courseId) {
      return;
    }
    setGenerating(true);
    try {
      const result = await rpx.aiCheckoutBuilder.generateCheckoutPage({
        courseId: state.courseId,
        marketingText: `Generate a POST-PURCHASE THANK YOU page. The customer just completed their purchase. I need:
- A celebratory headline (short, warm)
- A subtitle (one line)
- Body HTML content (what they get, what happens next, how to get started)
- CTA button text

Return these as a JSON object with sections: a "hero" section (headline + subtitle) and a "content" section (with HTML in a single paragraph block). Keep the tone warm, congratulatory, and action-oriented.

Here's the product/event info:
${text.trim()}`,
        priceInfo: {
          name: state.courseTitle || 'Course',
          amount: 0,
          paymentType: 'onetime',
        },
      });

      const sections = result.config.sections || [];
      const hero = sections.find((s) => s.type === 'hero') as any;
      const content = sections.find((s) => s.type === 'content') as any;

      if (hero?.content) {
        updateState({
          welcomeHeadline: hero.content.headline || '',
          welcomeSubtitle: hero.content.subtitle || '',
        });
      }
      if (content?.content?.blocks) {
        const html = content.content.blocks
          .map((b: any) => {
            if (b.type === 'heading') {
              return `<h3>${b.text}</h3>`;
            }
            if (b.type === 'benefit-list' && b.items) {
              return `<ul>${b.items.map((i: string) => `<li>${i}</li>`).join('')}</ul>`;
            }
            return `<p>${b.text || ''}</p>`;
          })
          .join('\n');
        updateState({ welcomeBodyHtml: html });
      }

      const cta = sections.find((s) => s.type === 'cta') as any;
      if (cta?.content?.acceptText) {
        updateState({ welcomeCtaText: cta.content.acceptText });
      }

      setCollapsed(true);
    } catch (err) {
      showError(err);
    } finally {
      setGenerating(false);
    }
  }

  if (collapsed) {
    return (
      <div class="mb-4 flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
        <span class="text-green-700 font-medium">
          Content generated. Edit the fields below to customize.
        </span>
        <button
          type="button"
          class="text-green-600 underline text-sm"
          onClick={() => setCollapsed(false)}
        >
          Generate again
        </button>
      </div>
    );
  }

  return (
    <div class="mb-5 bg-indigo-50/50 rounded-xl p-5">
      <div class="flex items-center gap-2 mb-1">
        <IcoSparkles class="size-4 text-indigo-400" />
        <h4 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">AI Generate</h4>
      </div>
      <p class="text-sm text-gray-500 mb-3">
        Describe your product or event. AI will generate the headline, subtitle, body content, and
        CTA.
      </p>
      <textarea
        class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-24 mb-3"
        placeholder="Describe what the buyer just purchased and what they should do next..."
        value={text}
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
      />
      <div class="flex items-center justify-between">
        <span class="text-xs text-gray-400">
          {text.length > 0 ? `${text.length.toLocaleString()} characters` : ''}
        </span>
        <BtnPrimary
          onClick={handleGenerate}
          isLoading={generating}
          disabled={!text.trim() || !state.courseId}
        >
          {generating ? 'Generating...' : 'Generate Content'}
        </BtnPrimary>
      </div>
    </div>
  );
}

function stateToWelcomeConfig(state: WizardState): MiraseeWelcomePageConfig {
  return {
    headline: state.welcomeHeadline || 'Welcome!',
    subtitle: state.welcomeSubtitle || undefined,
    bodyHtml: trimEmptyHtml(state.welcomeBodyHtml),
    upsellBodyHtml: trimEmptyHtml(state.welcomeUpsellBodyHtml),
    ctaText: state.welcomeCtaText || undefined,
    ctaUrl: resolveCtaUrl(state) || undefined,
    videoUrl: state.welcomeVideoUrl || undefined,
    videoCaption: state.welcomeVideoCaption || undefined,
    calendar: state.welcomeCalendar,
    smsOptin: state.welcomeSmsOptin,
  };
}

function stateToCheckoutConfig(state: WizardState): MiraseeCheckoutConfig {
  return {
    templateMode: 'mirasee-checkout',
    hero: { headline: state.welcomeHeadline || 'Welcome!' },
  };
}

function PreviewModal({ state, onClose }: { state: WizardState; onClose(): void }) {
  const [previewUpsell, setPreviewUpsell] = useState(false);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const rawWelcomeConfig = stateToWelcomeConfig(state);
  const config = stateToCheckoutConfig(state);
  const welcomeConfig = resolveWelcomePageTokens(rawWelcomeConfig, {
    parentDeferredDate: state.deferredDate || undefined,
    upsellDeferredDate: state.upsellDeferredDate || undefined,
  });
  const previewBodyHtml =
    previewUpsell && welcomeConfig.upsellBodyHtml
      ? welcomeConfig.upsellBodyHtml
      : welcomeConfig.bodyHtml;

  return (
    <div style="position: fixed; inset: 0; z-index: 9999; background: #fff; overflow-y: auto">
      <div style="position: sticky; top: 0; z-index: 10; background: rgba(0,0,0,0.8); display: flex; align-items: center; justify-content: space-between; padding: 8px 16px">
        <div class="flex items-center gap-3">
          <span style="color: #fff; font-size: 13px; font-weight: 500">Thank-you page preview</span>
          {welcomeConfig.upsellBodyHtml && (
            <button
              type="button"
              onClick={() => setPreviewUpsell(!previewUpsell)}
              style="background: rgba(255,255,255,0.15); color: #fff; border: 1px solid rgba(255,255,255,0.3); border-radius: 6px; padding: 4px 12px; font-size: 12px; font-weight: 500; cursor: pointer"
            >
              {previewUpsell ? 'Viewing: Course + Upsell' : 'Viewing: Course Only'}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          style="background: #fff; color: #111; border: none; border-radius: 6px; padding: 6px 16px; font-size: 13px; font-weight: 600; cursor: pointer"
        >
          Close Preview
        </button>
      </div>
      <WelcomeShell
        config={config}
        welcomeConfig={welcomeConfig}
        courseTitle={state.courseTitle}
        brand={state.brand}
      >
        {welcomeConfig.videoUrl && (
          <VideoEmbed url={welcomeConfig.videoUrl} caption={welcomeConfig.videoCaption} />
        )}

        {previewBodyHtml && (
          <div
            class="welcome-body-html"
            style="font-size: 16px; line-height: 1.7; color: #4b5563; margin-bottom: 28px; text-align: left"
            dangerouslySetInnerHTML={{ __html: previewBodyHtml }}
          />
        )}

        {welcomeConfig.calendar && welcomeConfig.calendar.startDate && (
          <AddToCalendarDropdown
            title={welcomeConfig.calendar.title}
            startDate={welcomeConfig.calendar.startDate}
            durationMinutes={welcomeConfig.calendar.durationMinutes}
            description={welcomeConfig.calendar.description}
            recurrence={welcomeConfig.calendar.recurrence}
            location={welcomeConfig.calendar.location}
          />
        )}

        {welcomeConfig.smsOptin && (
          <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; margin: 24px 0">
            {welcomeConfig.smsOptin.heading && (
              <h3 style="font-size: 17px; font-weight: 700; color: #1e293b; margin: 0 0 6px; text-align: center">
                {welcomeConfig.smsOptin.heading}
              </h3>
            )}
            {welcomeConfig.smsOptin.description && (
              <p style="font-size: 14px; color: #64748b; margin: 0 0 16px; text-align: center; line-height: 1.5">
                {welcomeConfig.smsOptin.description}
              </p>
            )}
            <div style="display: flex; gap: 8px; max-width: 400px; margin: 0 auto">
              <input
                type="tel"
                placeholder="(555) 123-4567"
                disabled
                style="flex: 1; padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 15px; opacity: 0.6"
              />
              <button
                type="button"
                disabled
                style="padding: 10px 20px; border-radius: 8px; background: #003da6; color: #fff; font-weight: 600; font-size: 14px; border: none; white-space: nowrap; opacity: 0.6"
              >
                {welcomeConfig.smsOptin.buttonText || 'Yes, text me reminders'}
              </button>
            </div>
            <label style="display: flex; gap: 8px; align-items: flex-start; margin-top: 12px; cursor: pointer; max-width: 400px; margin-left: auto; margin-right: auto">
              <input
                type="checkbox"
                disabled
                style="margin-top: 3px; flex-shrink: 0; opacity: 0.6"
              />
              <span style="font-size: 12px; color: #64748b; line-height: 1.4">
                By confirming, you consent to receive automated text messages from Mirasee Learning
                at the number provided, including reminders about sessions, live events, and program
                updates. Message frequency varies. Msg &amp; data rates may apply. Reply HELP for
                help or STOP to cancel at any time. Consent is not a condition of purchase.
              </span>
            </label>
          </div>
        )}

        {welcomeConfig.ctaText && (
          <span style="display: inline-block; padding: 16px 48px; border-radius: 8px; background: linear-gradient(135deg, #ff6b00 0%, #ffc600 100%); color: #1a1a1a; font-size: 17px; font-weight: 700; box-shadow: 0 2px 8px rgba(255,107,0,0.3)">
            {welcomeConfig.ctaText}
          </span>
        )}
      </WelcomeShell>
    </div>
  );
}

export function ThankyouStep({ state, updateState }: Props) {
  const selected = currentSelection(state);
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div>
      <div class="flex items-start justify-between mb-5">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 mb-1">Thank-You Page</h2>
          <p class="text-sm text-gray-500">Configure what buyers see after completing checkout.</p>
        </div>
        {state.welcomeEnabled && (
          <BtnSecondary onClick={() => setShowPreview(true)} class="gap-2 flex-shrink-0">
            <IcoEye class="size-4" />
            Preview
          </BtnSecondary>
        )}
      </div>
      <div class="rounded-lg bg-blue-50 border border-blue-200 p-3 mb-4 text-sm text-blue-900">
        The Thank-You page below applies to <strong>all prices</strong> in this campaign. Edit once
        and every buyer (Full Pay, Payment Plan, etc.) will see the same content.
      </div>
      {!state.welcomeEnabled && (
        <div class="rounded-xl border-2 border-dashed border-gray-200 p-8 text-center">
          <IcoDocument class="size-8 text-gray-300 mx-auto mb-3" />
          <h3 class="text-base font-semibold text-gray-800 mb-1">Add a Thank-You Page</h3>
          <p class="text-sm text-gray-500 mb-4 max-w-md mx-auto">
            Show buyers a branded welcome message after purchase — with a video, next steps, and a
            call-to-action.
          </p>
          <BtnPrimary onClick={() => updateState({ welcomeEnabled: true })}>
            Set Up Thank-You Page
          </BtnPrimary>
        </div>
      )}

      {state.welcomeEnabled && (
        <div class="flex flex-col gap-5">
          <AIGeneratePanel state={state} updateState={updateState} />

          <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
            <div class="flex items-center gap-2">
              <IcoDocument class="size-4 text-gray-400" />
              <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Content</h3>
            </div>
            <p class="text-xs text-gray-500 -mt-1">
              Type{' '}
              <code class="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] text-gray-700">
                {'{{deferred_enrollment_date}}'}
              </code>{' '}
              in your headline, subtitle, or body to auto-insert your campaign's start date (e.g.,
              May 4, 2026). It renders blank if your campaign has no deferred date.
            </p>
            <label class="flex flex-col gap-1.5 max-w-lg">
              <span class="text-sm font-medium text-gray-700">Headline</span>
              <textarea
                value={state.welcomeHeadline}
                onInput={(e) =>
                  updateState({ welcomeHeadline: (e.target as HTMLTextAreaElement).value })
                }
                placeholder="Welcome! You're all set."
                rows={1}
                class="border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                style="field-sizing: content"
              />
            </label>

            <label class="flex flex-col gap-1.5 max-w-lg">
              <span class="text-sm font-medium text-gray-700">Subtitle</span>
              <textarea
                value={state.welcomeSubtitle}
                onInput={(e) =>
                  updateState({ welcomeSubtitle: (e.target as HTMLTextAreaElement).value })
                }
                placeholder="Here's what happens next..."
                rows={1}
                class="border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                style="field-sizing: content"
              />
            </label>

            <RichHtmlField
              label={state.upsellEnabled ? 'Body — course only' : 'Body'}
              labelClass="text-sm font-medium text-gray-700"
              hint={
                state.upsellEnabled
                  ? 'Shown when the student declines the upsell or no upsell is offered.'
                  : undefined
              }
              placeholderText="Thank you for joining..."
              textareaClass="border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono min-h-40"
              value={state.welcomeBodyHtml}
              onChange={(html) => updateState({ welcomeBodyHtml: html })}
              minHeightClass="*:min-h-40"
            />
            {state.upsellEnabled && (
              <RichHtmlField
                label="Body — with upsell"
                labelClass="text-sm font-medium text-gray-700"
                hint="Shown when the student accepts the upsell. If empty, the body above is used."
                placeholderText="Welcome! You now have access to both..."
                textareaClass="border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono min-h-40"
                value={state.welcomeUpsellBodyHtml}
                onChange={(html) => updateState({ welcomeUpsellBodyHtml: html })}
                minHeightClass="*:min-h-40"
              />
            )}
          </div>

          <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
            <div class="flex items-center gap-2">
              <IcoVideoCamera class="size-4 text-gray-400" />
              <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">Video</h3>
            </div>
            <label class="flex flex-col gap-1.5 max-w-lg">
              <span class="text-sm font-medium text-gray-700">Video URL</span>
              <input
                type="text"
                value={state.welcomeVideoUrl}
                onInput={(e) =>
                  updateState({ welcomeVideoUrl: (e.target as HTMLInputElement).value })
                }
                placeholder="Vimeo, YouTube, or Wistia URL"
                class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </label>
            <label class="flex flex-col gap-1.5 max-w-lg">
              <span class="text-sm font-medium text-gray-700">Caption (optional)</span>
              <input
                type="text"
                value={state.welcomeVideoCaption}
                onInput={(e) =>
                  updateState({ welcomeVideoCaption: (e.target as HTMLInputElement).value })
                }
                placeholder="Watch the welcome video"
                class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </label>
          </div>

          <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <IcoCalendar class="size-4 text-gray-400" />
                <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                  Calendar
                </h3>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!state.welcomeCalendar}
                class={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors ${
                  state.welcomeCalendar ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
                onClick={() => {
                  if (state.welcomeCalendar) {
                    updateState({ welcomeCalendar: undefined });
                  } else {
                    updateState({
                      welcomeCalendar: {
                        title: state.courseTitle || '',
                        startDate: '',
                        durationMinutes: 60,
                      },
                    });
                  }
                }}
              >
                <span
                  class={`pointer-events-none inline-block size-5 rounded-full bg-white shadow-sm transition-transform ${
                    state.welcomeCalendar ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {state.welcomeCalendar && (
              <div class="flex flex-col gap-3">
                <label class="flex flex-col gap-1.5 max-w-lg">
                  <span class="text-sm font-medium text-gray-700">Event Title</span>
                  <input
                    type="text"
                    value={state.welcomeCalendar.title}
                    onInput={(e) =>
                      updateState({
                        welcomeCalendar: {
                          ...state.welcomeCalendar!,
                          title: (e.target as HTMLInputElement).value,
                        },
                      })
                    }
                    placeholder="Effortless AI Event"
                    class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </label>
                <label class="flex flex-col gap-1.5 max-w-lg">
                  <span class="text-sm font-medium text-gray-700">Start Date & Time (Eastern)</span>
                  <input
                    type="datetime-local"
                    value={state.welcomeCalendar.startDate}
                    onInput={(e) =>
                      updateState({
                        welcomeCalendar: {
                          ...state.welcomeCalendar!,
                          startDate: (e.target as HTMLInputElement).value,
                        },
                      })
                    }
                    class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </label>
                <label class="flex flex-col gap-1.5 max-w-lg">
                  <span class="text-sm font-medium text-gray-700">Duration</span>
                  <select
                    value={state.welcomeCalendar.durationMinutes}
                    onChange={(e) =>
                      updateState({
                        welcomeCalendar: {
                          ...state.welcomeCalendar!,
                          durationMinutes: parseInt((e.target as HTMLSelectElement).value, 10),
                        },
                      })
                    }
                    class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="120">2 hours</option>
                    <option value="180">3 hours</option>
                    <option value="360">6 hours</option>
                    <option value="480">8 hours</option>
                  </select>
                </label>
                <label class="flex flex-col gap-1.5 max-w-lg">
                  <span class="text-sm font-medium text-gray-700">Repeats</span>
                  <select
                    value={state.welcomeCalendar.recurrence || ''}
                    onChange={(e) =>
                      updateState({
                        welcomeCalendar: {
                          ...state.welcomeCalendar!,
                          recurrence: (e.target as HTMLSelectElement).value || undefined,
                        },
                      })
                    }
                    class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Doesn't repeat</option>
                    <option value="RRULE:FREQ=DAILY;COUNT=2">Daily for 2 days</option>
                    <option value="RRULE:FREQ=DAILY;COUNT=3">Daily for 3 days</option>
                    <option value="RRULE:FREQ=DAILY;COUNT=5">Daily for 5 days</option>
                  </select>
                </label>
                <label class="flex flex-col gap-1.5 max-w-lg">
                  <span class="text-sm font-medium text-gray-700">
                    Location / Join URL (optional)
                  </span>
                  <input
                    type="text"
                    value={state.welcomeCalendar.location || ''}
                    onInput={(e) =>
                      updateState({
                        welcomeCalendar: {
                          ...state.welcomeCalendar!,
                          location: (e.target as HTMLInputElement).value || undefined,
                        },
                      })
                    }
                    placeholder="https://mirasee.com/2026/eai-day-1-starting-soon"
                    class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </label>
                <label class="flex flex-col gap-1.5">
                  <span class="text-sm font-medium text-gray-700">Description (optional)</span>
                  <textarea
                    value={state.welcomeCalendar.description || ''}
                    onInput={(e) =>
                      updateState({
                        welcomeCalendar: {
                          ...state.welcomeCalendar!,
                          description: (e.target as HTMLTextAreaElement).value,
                        },
                      })
                    }
                    placeholder="Join us for this live training event..."
                    rows={3}
                    class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </label>
              </div>
            )}
          </div>

          <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <IcoBell class="size-4 text-gray-400" />
                <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                  SMS Opt-in
                </h3>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!state.welcomeSmsOptin}
                class={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors ${
                  state.welcomeSmsOptin ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
                onClick={() => {
                  if (state.welcomeSmsOptin) {
                    updateState({ welcomeSmsOptin: undefined });
                  } else {
                    updateState({
                      welcomeSmsOptin: {
                        heading: 'Stay in the loop with Mirasee Learning',
                        description:
                          'Get text reminders about your upcoming sessions, live events, and important updates from Mirasee Learning.',
                        buttonText: 'Yes, text me reminders',
                      },
                    });
                  }
                }}
              >
                <span
                  class={`pointer-events-none inline-block size-5 rounded-full bg-white shadow-sm transition-transform ${
                    state.welcomeSmsOptin ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {state.welcomeSmsOptin && (
              <div class="flex flex-col gap-3">
                <label class="flex flex-col gap-1.5 max-w-lg">
                  <span class="text-sm font-medium text-gray-700">Heading</span>
                  <input
                    type="text"
                    value={state.welcomeSmsOptin.heading || ''}
                    onInput={(e) =>
                      updateState({
                        welcomeSmsOptin: {
                          ...state.welcomeSmsOptin!,
                          heading: (e.target as HTMLInputElement).value,
                        },
                      })
                    }
                    placeholder="Stay in the loop with Mirasee Learning"
                    class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </label>
                <label class="flex flex-col gap-1.5">
                  <span class="text-sm font-medium text-gray-700">Description</span>
                  <textarea
                    value={state.welcomeSmsOptin.description || ''}
                    onInput={(e) =>
                      updateState({
                        welcomeSmsOptin: {
                          ...state.welcomeSmsOptin!,
                          description: (e.target as HTMLTextAreaElement).value,
                        },
                      })
                    }
                    placeholder="Get text reminders about your upcoming sessions..."
                    rows={2}
                    class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </label>
                <label class="flex flex-col gap-1.5 max-w-lg">
                  <span class="text-sm font-medium text-gray-700">SMS Button Text</span>
                  <input
                    type="text"
                    value={state.welcomeSmsOptin.buttonText || ''}
                    onInput={(e) =>
                      updateState({
                        welcomeSmsOptin: {
                          ...state.welcomeSmsOptin!,
                          buttonText: (e.target as HTMLInputElement).value,
                        },
                      })
                    }
                    placeholder="Yes, text me reminders"
                    class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </label>
                <div class="flex flex-col gap-1.5">
                  <span class="text-sm font-medium text-gray-700">Ontraport Tag</span>
                  <p class="text-xs text-gray-500">
                    Apply this tag in Ontraport when a student opts in to SMS.
                  </p>
                  <OntraportTagPicker
                    courseId={state.courseId}
                    selectedTags={state.welcomeSmsOptin.tag ? [state.welcomeSmsOptin.tag] : []}
                    onChange={(tags) =>
                      updateState({
                        welcomeSmsOptin: {
                          ...state.welcomeSmsOptin!,
                          tag: tags[tags.length - 1] || undefined,
                        },
                      })
                    }
                    placeholder="Search for an Ontraport tag..."
                  />
                </div>
              </div>
            )}
          </div>

          {(() => {
            // Absent means on, as the page has always asked a brand-new
            // account for a password; the switch stores an explicit off.
            const setPasswordOn = state.welcomeSetPassword?.enabled !== false;
            const setPassword = state.welcomeSetPassword;
            return (
              <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <IcoLock class="size-4 text-gray-400" />
                    <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                      Set Password
                    </h3>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={setPasswordOn}
                    class={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors ${
                      setPasswordOn ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                    onClick={() =>
                      updateState({
                        welcomeSetPassword: setPasswordOn
                          ? { enabled: false }
                          : {
                              enabled: true,
                              position: 'bottom',
                              heading: 'Create your password.',
                              description:
                                "You'll use it to login to our portal where you'll access {{course_title}}. Set it now and you'll be taken straight to your course.",
                            },
                      })
                    }
                  >
                    <span
                      class={`pointer-events-none inline-block size-5 rounded-full bg-white shadow-sm transition-transform ${
                        setPasswordOn ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                <p class="text-xs text-gray-500">
                  Shown only to a buyer whose account was just created. After they set a password
                  they go to the CTA link below. If this is off, they are emailed a set-password
                  link instead. <code class="font-mono">{'{{course_title}}'}</code> in the text
                  becomes the course name.
                </p>
                {setPasswordOn && (
                  <div class="flex flex-col gap-3">
                    <label class="flex flex-col gap-1.5 max-w-lg">
                      <span class="text-sm font-medium text-gray-700">Position</span>
                      <select
                        value={setPassword?.position || 'bottom'}
                        onChange={(e) =>
                          updateState({
                            welcomeSetPassword: {
                              ...setPassword,
                              enabled: true,
                              position: (e.target as HTMLSelectElement).value as
                                | 'bottom'
                                | 'afterBody',
                            },
                          })
                        }
                        class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="bottom">Bottom of page</option>
                        <option value="afterBody">Under body copy</option>
                      </select>
                      <span class="text-xs text-gray-500">
                        Bottom of page: after everything else, where the button goes. Under body
                        copy: just above the calendar section of your body, or after the body if
                        there isn't one (put <code class="font-mono">{'{{set_password}}'}</code> in
                        the body HTML to force a spot).
                      </span>
                    </label>
                    <label class="flex flex-col gap-1.5 max-w-lg">
                      <span class="text-sm font-medium text-gray-700">Heading</span>
                      <input
                        type="text"
                        value={setPassword?.heading || ''}
                        onInput={(e) =>
                          updateState({
                            welcomeSetPassword: {
                              ...setPassword,
                              enabled: true,
                              heading: (e.target as HTMLInputElement).value,
                            },
                          })
                        }
                        placeholder="Create your password."
                        class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label class="flex flex-col gap-1.5">
                      <span class="text-sm font-medium text-gray-700">Description</span>
                      <textarea
                        value={setPassword?.description || ''}
                        onInput={(e) =>
                          updateState({
                            welcomeSetPassword: {
                              ...setPassword,
                              enabled: true,
                              description: (e.target as HTMLTextAreaElement).value,
                            },
                          })
                        }
                        placeholder="You'll use it to login to our portal where you'll access {{course_title}}. Set it now and you'll be taken straight to your course."
                        rows={2}
                        class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                  </div>
                )}
              </div>
            );
          })()}

          <div class="rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4">
            <div class="flex items-center gap-2">
              <IcoExternalLink class="size-4 text-gray-400" />
              <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                Call to Action
              </h3>
            </div>
            <label class="flex flex-col gap-1.5 max-w-lg">
              <span class="text-sm font-medium text-gray-700">CTA Button Text</span>
              <input
                type="text"
                value={state.welcomeCtaText}
                onInput={(e) =>
                  updateState({ welcomeCtaText: (e.target as HTMLInputElement).value })
                }
                placeholder="Go to your course"
                class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </label>
            <div class="flex flex-col gap-1.5 max-w-lg">
              <span class="text-sm font-medium text-gray-700">Destination</span>
              <select
                value={selected}
                onChange={(e) => {
                  const val = (e.target as HTMLSelectElement).value;
                  if (val === 'custom') {
                    updateState({ welcomeCtaUrl: '' });
                  } else {
                    updateState({ welcomeCtaUrl: val });
                  }
                }}
                class="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {CTA_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {selected === 'custom' && (
                <input
                  type="text"
                  value={state.welcomeCtaUrl}
                  onInput={(e) =>
                    updateState({ welcomeCtaUrl: (e.target as HTMLInputElement).value })
                  }
                  placeholder="/some/page or https://example.com"
                  class="border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1"
                />
              )}
              <p class="text-xs text-gray-500 mt-1">
                Where the button goes. With the Set Password block on, a brand-new buyer is sent
                here after creating their password. Someone who bought for an existing account
                without signing in is sent through sign-in, then here.
              </p>
            </div>
          </div>

          <div class="flex items-center gap-4">
            <BtnSecondary onClick={() => setShowPreview(true)}>Preview Page</BtnSecondary>
            <button
              type="button"
              onClick={() => updateState({ welcomeEnabled: false })}
              class="text-xs text-gray-400 hover:text-gray-600"
            >
              Disable custom page
            </button>
          </div>

          {showPreview && <PreviewModal state={state} onClose={() => setShowPreview(false)} />}
        </div>
      )}
    </div>
  );
}
